import { useState } from 'react';
import { message } from 'antd';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { AXIOS_INSTANCE } from '@/shared/api/mutator/custom-instance';

interface MigrationResult {
  successfulMigrations: string[];
  errors: Record<string, string>;
  migrationTime: string;
  successCount: number;
  errorCount: number;
  totalProcessed: number;
}

interface ExportQueryResult {
  query: string;
  description: string;
  instructions: string;
}

interface CsvFormatResult {
  headers: string[];
  descriptions: string[];
  example: string;
  notes: string[];
}

interface MigrationStatus {
  enabled: boolean;
  service: string;
  description: string;
  supportedFormats: string[];
  requiredPermissions: string[];
}

export const useUserMigration = () => {
  const [loading, setLoading] = useState(false);

  /**
   * Check if migration is enabled and available using React Query
   */
  const {
    data: migrationStatus,
    isLoading: statusLoading,
    error: statusError,
    refetch: refetchStatus
  } = useQuery({
    queryKey: ['migration-status'],
    queryFn: async (): Promise<MigrationStatus> => {
      try {
        console.log('Checking migration status...');
        const response = await AXIOS_INSTANCE.get<MigrationStatus>('/api/nub/users/migration/status');
        console.log('Migration status response:', response.data);
        return response.data;
      } catch (error) {
        console.error('Migration status error:', error);
        throw error;
      }
    },
    retry: (failureCount, error) => {
      // Don't retry if migration is disabled (404/403)
      const axiosError = error as AxiosError;
      if (axiosError.response?.status === 404 || axiosError.response?.status === 403) {
        return false;
      }
      return failureCount < 2;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false
  });

  const migrationEnabled = (migrationStatus as MigrationStatus | undefined)?.enabled ?? false;

  /**
   * Import users from CSV file using React Query mutation
   */
  const importMutation = useMutation({
    mutationFn: async (file: File): Promise<MigrationResult> => {
      const formData = new FormData();
      formData.append('file', file);

      const response = await AXIOS_INSTANCE.post<MigrationResult>(
          '/api/nub/users/migration/import-csv',
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
      );

      return response.data;
    },
    onSuccess: (data) => {
      message.success(`Migration completed! ${data.successCount} users imported successfully.`);
      if (data.errorCount > 0) {
        message.warning(`${data.errorCount} users were skipped or failed to import.`);
      }
    },
    onError: (error: AxiosError) => {
      const errorMessage = (error.response?.data as any)?.message || error.message || 'Migration failed';
      message.error(`Migration failed: ${errorMessage}`);
    },
  });

  /**
   * Get SQL query for exporting legacy user data using React Query
   */
  const {
    data: exportQuery,
    isLoading: exportQueryLoading,
    refetch: refetchExportQuery
  } = useQuery({
    queryKey: ['migration-export-query'],
    queryFn: async (): Promise<ExportQueryResult> => {
      const response = await AXIOS_INSTANCE.get<ExportQueryResult>('/api/nub/users/migration/export-query');
      return response.data;
    },
    enabled: migrationEnabled,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });

  /**
   * Get CSV format documentation using React Query
   */
  const {
    data: csvFormat,
    isLoading: csvFormatLoading,
    refetch: refetchCsvFormat
  } = useQuery({
    queryKey: ['migration-csv-format'],
    queryFn: async (): Promise<CsvFormatResult> => {
      const response = await AXIOS_INSTANCE.get<CsvFormatResult>('/api/nub/users/migration/csv-format');
      return response.data;
    },
    enabled: migrationEnabled,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });

  /**
   * Download export query as SQL file
   */
  const downloadExportQuery = async (): Promise<void> => {
    try {
      if (!exportQuery) {
        await refetchExportQuery();
        return;
      }

      const sqlContent = `-- Legacy User Export Query for ScaleFi Migration
-- Generated on: ${new Date().toISOString()}
-- Instructions: ${exportQuery.instructions}

${exportQuery.query}`;

      const blob = new Blob([sqlContent], { type: 'text/sql' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `legacy-user-export-${new Date().toISOString().split('T')[0]}.sql`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      message.success('Export query downloaded successfully');
    } catch (error: any) {
      message.error('Failed to download export query');
    }
  };

  /**
   * Download CSV format template
   */
  const downloadCsvTemplate = async (): Promise<void> => {
    try {
      if (!csvFormat) {
        await refetchCsvFormat();
        return;
      }

      // Create CSV template with headers and example row
      const csvContent = [
        csvFormat.headers.join(','),
        '# Example row (remove this line):',
        csvFormat.example,
        '# Add your data below:'
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `user-migration-template-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      message.success('CSV template downloaded successfully');
    } catch (error: any) {
      message.error('Failed to download CSV template');
    }
  };

  /**
   * Validate CSV file before upload
   */
  const validateCsvFile = (file: File | undefined): { valid: boolean; message?: string } => {
    // Check if file exists
    if (!file) {
      return { valid: false, message: 'No file provided' };
    }

    // Check file type
    if (!file.type.includes('csv') && !file.name.toLowerCase().endsWith('.csv')) {
      return { valid: false, message: 'Please select a valid CSV file' };
    }

    // Check file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      return { valid: false, message: 'File size must be less than 10MB' };
    }

    // Check if file is empty
    if (file.size === 0) {
      return { valid: false, message: 'File cannot be empty' };
    }

    return { valid: true };
  };

  /**
   * Parse CSV file and preview data (first few rows)
   */
  const previewCsvData = (file: File): Promise<string[][]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const text = e.target?.result as string;
          const lines = text.split('\n').slice(0, 6); // First 6 lines for preview
          const data = lines.map(line => line.split(','));
          resolve(data);
        } catch (error) {
          reject(new Error('Failed to parse CSV file'));
        }
      };

      reader.onerror = () => {
        reject(new Error('Failed to read file'));
      };

      reader.readAsText(file);
    });
  };

  return {
    // State
    migrationEnabled,
    migrationStatus,
    exportQuery,
    csvFormat,

    // Loading states
    statusLoading,
    exportQueryLoading,
    csvFormatLoading,
    importLoading: importMutation.isPending,

    // Errors
    statusError,
    importError: importMutation.error,

    // Actions
    importUsersFromCsv: importMutation.mutate,
    importUsersFromCsvAsync: importMutation.mutateAsync,
    refetchStatus,
    refetchExportQuery,
    refetchCsvFormat,
    downloadExportQuery,
    downloadCsvTemplate,
    validateCsvFile,
    previewCsvData,

    // Results
    importResult: importMutation.data,
    isImportSuccess: importMutation.isSuccess,
    resetImport: importMutation.reset,
  };
};